import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { APP_INITIALIZER, ErrorHandler, enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core'
import { bootstrapApplication } from '@angular/platform-browser'
import {
  PreloadAllModules,
  RouteReuseStrategy,
  Router,
  provideRouter,
  withPreloading,
} from '@angular/router'
import { JwtInterceptor } from '@app/interceptors/jwt.interceptor'
import { TimeoutInterceptor } from '@app/interceptors/timeout.interceptor'
import { VideoComponent } from '@app/video.component'
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone'
import { AngularFireModule } from '@angular/fire/compat'
import { AngularFireDatabaseModule } from '@angular/fire/compat/database'
import { IonicStorageModule } from '@ionic/storage-angular'
import * as SentrySibling from '@sentry/angular'
import * as Sentry from '@sentry/capacitor'
import { RewriteFrames as RewriteFramesIntegration } from '@sentry/integrations'
import { BrowserTracing } from '@sentry/tracing'
import { AuthService } from '@service/auth.service'
import { FabricService } from '@service/fabric.service'
import { AppComponent } from './app/app.component'
import { routes } from './app/app.routes'
import { GlobalErrorHandler } from './app/global-error-handler'
import { environment } from './environments/environment'

const appInitFactory = (auth: AuthService, fabricService: FabricService) => () => {
  const timeout = new Promise(resolve => setTimeout(resolve, 10000))
  return Promise.race([
    Promise.all([auth.init(), fabricService.init()]),
    timeout,
  ])
}

Sentry.init(
  {
    debug: environment.production,
    release: `${environment.env}-${environment.version}`,
    dist: 'kiwi3',
    environment: environment.env,
    dsn: environment.sentryDsn,
    integrations: [
      new BrowserTracing(),
      new RewriteFramesIntegration({
        iteratee: frame => ({
          ...frame,
          filename: frame.filename.replace(/^.*localhost\//, 'http://app/'),
        }),
      }),
    ] as any[],
    beforeSend(event: any, hint: any) {
      // http code가 다음과 같으면 센트리 로깅하지 않음
      if (hint.originalException && hint.originalException.status === 0) {
        return null
      }
      // console.log(hint)
      // return event
    },
  },
  SentrySibling.init,
)

if (environment.production) enableProdMode()

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      IonicStorageModule.forRoot(),
      AngularFireModule.initializeApp({
        databaseURL: environment.fireDatabaseURI,
        apiKey: '1AIzaSyCuI563oFqM_vkNTjLiTxN3qxR5f2gorho',
      }),
      AngularFireDatabaseModule,
    ),
    provideIonicAngular({
      innerHTMLTemplatesEnabled: true,
      rippleEffect: false,
      // swipeBackEnabled: false,
      // 안드로이드에만 nav 적용
      // ...(isPlatform('android') ? { navAnimation } : {}),
      // navAnimation,
      // navAnimation: mdTransitionAnimation
    }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitFactory,
      deps: [AuthService, FabricService],
      multi: true,
    },

    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },
    {
      provide: SentrySibling.TraceService,
      deps: [Router],
    },
    {
      provide: ErrorHandler,
      useValue: SentrySibling.createErrorHandler({}),
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
  .then(app => {
    app.bootstrap(VideoComponent)
  })
  .catch(err => console.log(err))
